import React, { useContext, useEffect, useState } from "react";
import Select from "react-select";
import "../styles/market.css";
import { Context } from "../pages/LoginMarket";
import { Card, CardBody, CardHeader, Label, Button, Spinner } from "reactstrap";
import { DataWithColorSeriesTypes, DataPointTypes } from "../types";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  fetchUSNAvgPriceTrend,
  fetchUSNMarketAvgPrice,
  fetchUSNStateProviders,
  fetchUsnPtcRate,
} from "../api/usnMarket";
import { selectUtility } from "../store/utility/selector";
import DataTable from "react-data-table-component";
import { PriceComparisionChart } from "./Charts/PriceComparisionChart";
import { Typography } from "@mui/material";
import { PriceBarChart } from "./Charts/PriceBarChart";
import { selectMyProvider } from "../store/myProvider/selector";
import Flatpickr from "react-flatpickr";
// import Datepicker from "./Datepicker";

interface searchOptionObj {
  value: any;
  label: any;
}

const today = moment().format("YYYY-MM-DD");

const calDate = (val: string) => {
  return moment(val, "YYYY-MM-DD").subtract(30, "days").format("YYYY-MM-DD");
};

const toChartsData = (inputData: any, outputData: any) => {
  if (inputData) {
    return inputData.map((item: any) => {
      if (item.date && +item.average_price !== 0) {
        return outputData.push({ x: item.date, y: +item.average_price });
      }
      return null;
    });
  } else {
    return (outputData = []);
  }
};

const MarketAvgRateUsn = () => {
  const currentLocation = useSelector<
    any,
    { state_id: string; state_name: string }
  >((state) => state.Location.currentLocation);
  const { provider, allProvider } = useContext(Context);
  const [compareProvider, setCompareProvider] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [filterValue, setFilterValue] = useState({
    date: `${calDate(today)}&${today}`,
  });
  const [data, setData] = useState<DataWithColorSeriesTypes[]>([]);
  const [last12MonthData, setLast12MonthData] = useState<any>([]);
  const [myAveragePriceData, setMyAveragePriceData] = useState<any>([]);
  const [ptcRateData, setPtcRateData] = useState<any>([]);
  const [providerList, setProviderList] = useState(["", provider.provider_id]);
  const [searchProviderList, setSearchProviderList] = useState<
    Array<searchOptionObj>
  >([]);
  const currentUtility = useSelector(selectUtility);
  const myProvider = useSelector(selectMyProvider);
  
  const [startDate, setStartDate] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(today);
  const getAvgPrice = async () => {
    setIsLoading(true);

    const promise = providerList.map((item) => {
      return fetchUSNMarketAvgPrice(
        currentLocation?.state_id,
        currentUtility.utility_id,
        filterValue.date,
        "1",
        +item || ""
      );
    });
    const res = await Promise.all(promise);
    const resData: any = res.map((item, index) => {
      if (item.status === 1) {
        return item.response.average_price;
      } else {
        return null;
      }
    });

    let avgData: DataPointTypes[] = [];
    let providerData: DataPointTypes[] = [];
    toChartsData(resData[0], avgData);
    toChartsData(resData[1], providerData);

    // Fetch PTC Rate data
    let ptcData: DataPointTypes[] = [];
    try {
      const ptcResponse = await fetchUsnPtcRate({
        utility_id: currentUtility.utility_id,
        state_id: currentLocation?.state_id,
        date: filterValue.date,
      });
      
      if (ptcResponse.status === 1 && ptcResponse.response) {
        ptcData = ptcResponse.response.map((item: any) => ({
          x: moment(item.date, "MM/DD/YYYY").format("MM/DD/YYYY"),
          y: parseFloat(item.rate),
        }));
        setPtcRateData(ptcData);
      }
    } catch (error) {
      console.error("Failed to fetch PTC Rate data:", error);
      setPtcRateData([]);
    }

    let newData = [
      {
        id: `${provider ? provider.provider_name : ""}`,
        data: providerData,
        color: "hsl(207,54.0%,46.9%)",
      },
      {
        id: "Market Average Rate",
        data: avgData,
        color: "hsl(118,40.6%,48.8%)",
      },
    ];

    if (ptcData.length > 0) {
      newData.push({
        id: "PTC Rate",
        data: ptcData,
        color: "#FACF0A",
      });
    }

    if (compareProvider) {
      try {
        const compareRes = await fetchUSNMarketAvgPrice(
          currentLocation?.state_id,
          currentUtility.utility_id,
          filterValue.date,
          "1",
          compareProvider.value
        );
        
        if (compareRes.status === 1) {
          let compareData: DataPointTypes[] = [];
          toChartsData(compareRes.response.average_price, compareData);
          
          newData.unshift({
            id: compareProvider.label || "",
            data: compareData,
            color: "hsl(359,79.5%,49.8%)",
          });
        }
      } catch (error) {
        console.error("Failed to fetch compare provider data:", error);
      }
    }

    setData(newData);
    setIsLoading(false);
  };
  const handleDownload = async () => {
    setIsDownload(true);
    try {
      const res = await fetchUSNAvgPriceTrend(
        currentLocation?.state_id,
        currentUtility.utility_id,
        "3",
        "",
        "",
        myProvider?.provider_id,
        true
      );
      const fileName = res.headers
        ?.get("content-disposition")
        ?.split("filename=")[1] || `MarketAvgRate_${currentLocation?.state_name || 'Unknown'}_${moment().format('YYYY-MM-DD')}.xlsx`;

      const data = await res.blob();

      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsDownload(false);
    } catch (error) {
      console.error("Download failed:", error);
      setIsDownload(false);
    }
  };

  const getLast12MonthsData = async () => {
    const res = await fetchUSNAvgPriceTrend(
      currentLocation?.state_id,
      currentUtility.utility_id,
      "3",
      "",
      "",
      myProvider?.provider_id,
      // false
    );

    if (res?.status === 1) {
      const { average_price, my_average_price } = res?.response || {};

      setLast12MonthData(
        average_price?.map((item: any) => ({
          date: moment(item.date, "MM/DD/YYYY").format("YYYY-MM-DD"),
          value: Number(item.average_price),
        })) || []
      );

      setMyAveragePriceData(
        my_average_price?.map((item: any) => ({
          date: moment(item.date, "MM/DD/YYYY").format("YYYY-MM-DD"),
          value: Number(item.average_price),
        })) || []
      );
    }
  };

  useEffect(() => {
    if (+provider.provider_id && allProvider.length && currentUtility) {
      getAvgPrice();
      getLast12MonthsData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, providerList, currentUtility]);

  useEffect(() => {
    const getStateProviders = () => {
      fetchUSNStateProviders(
        currentLocation?.state_id,
        "1",
        currentUtility?.utility_id
      ).then((res) => {
        const formattedList =
          res?.response?.map((item: any) => ({
            value: item.provider_id,
            label: item.provider_name,
          })) || [];
        setSearchProviderList(formattedList);
      });
    };

    if (currentLocation && currentUtility) {
      getStateProviders();
    }
  }, [currentLocation, currentUtility]);

  useEffect(() => {
    if (+provider.provider_id && allProvider.length) {
      setProviderList(["", provider.provider_id]);
      // setCompareName(allProvider[0]?.provider_name)
    } else {
      return;
    }
  }, [provider, allProvider]);

  useEffect(() => {
    if (compareProvider) {
      getAvgPrice();
    } else {
      getAvgPrice();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareProvider]);

  const changeDate = (val: any) => {
    setFilterValue({
      ...filterValue,
      date: `${calDate(moment(val).format("YYYY-MM-DD"))}&${moment(val).format(
        "YYYY-MM-DD"
      )}`,
    });
  };

  const handleStartDateChange = (date: any) => {
    const newStartDate = moment(date).format("YYYY-MM-DD");
    setStartDate(newStartDate);
    
    const maxEndDate = moment(newStartDate).add(30, "days").format("YYYY-MM-DD");
    const today = moment().format("YYYY-MM-DD");
    let newEndDate = endDate;
    if (moment(endDate).isAfter(maxEndDate)|| moment(newStartDate).isAfter(endDate)) {
      newEndDate = moment(maxEndDate).isAfter(today) ? today : maxEndDate;
      setEndDate(maxEndDate);
    }
    
    setFilterValue({
      ...filterValue,
      date: `${newStartDate}&${newEndDate}`,
    });
  };

  const handleEndDateChange = (date: any) => {
    const newEndDate = moment(date).format("YYYY-MM-DD");
    
    const minStartDate = moment(newEndDate).subtract(30, "days").format("YYYY-MM-DD");
    let newStartDate = startDate;
    if (moment(startDate).isBefore(minStartDate)) {
      newStartDate = minStartDate;
      setStartDate(minStartDate);
    }
    
    setEndDate(newEndDate);
    
    setFilterValue({
      ...filterValue,
      date: `${newStartDate}&${newEndDate}`,
    });
  };



  return (
    <Card style={{ minHeight: "600px" }}>
      <CardHeader className="align-items-center justify-content-between d-flex">
        <div>
          <h4 className="card-title mb-0 flex-grow-1">All In-Market Plans</h4>
          {currentLocation && currentUtility && (
            <Typography
              variant="subtitle2"
              className="text-muted"
            >{`${currentLocation.state_name} - ${currentUtility.utility_name}`}</Typography>
          )}
        </div>
        <div className="flex-shrink-0">
          {!isDownload ? (
            <Button
              color="secondary"
              onClick={handleDownload}
              className="btn-label"
            >
              <i className="bx bx-download label-icon align-middle fs-17 me-2"></i>
              <span>Download</span>
            </Button>
          ) : (
            <Button color="secondary" className="btn-load" outline>
              <span className="d-flex align-items-center">
                <Spinner size="sm" className="flex-shrink-0">
                  {" "}
                  Loading...{" "}
                </Spinner>
                <span className="flex-grow-1 ms-2">Loading...</span>
              </span>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody>
        <React.Fragment>
          <PriceBarChart
            title="Market Average Rate (Last 12 months)"
            data={last12MonthData}
            myAveragePriceData={myAveragePriceData}
            providerName={myProvider?.provider_name}
            isLoading={isLoading}
          />

          <div>
            <div style={{ display: "flex", gap: "20px", alignItems: "center", marginTop: "30px" }}>
              <div style={{ width: "240px" }}>
              <Label
              htmlFor="aria-example-input"
              className="form-label text-muted mb-0"
              style={{ fontWeight: "400" }}
            >
              Compare with
            </Label>
                <Select
                  defaultValue={searchProviderList[0]}
                  value={compareProvider}
                  isClearable
                  onChange={(val) => setCompareProvider(val)}
                  options={searchProviderList}
                />
              </div>
              
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <Label
                  htmlFor="start-date"
                  className="form-label text-muted mb-0"
                  style={{ fontWeight: "400" }}
                >
                  Start Date
                </Label>
                <Flatpickr
                  className="form-control"
                  value={startDate}
                  options={{
                    dateFormat: "Y-m-d",
                    maxDate: moment().format("YYYY-MM-DD"),
                    allowInput: true,
                  }}
                  disabled={isLoading}
                  onChange={([newValue]: any) => handleStartDateChange(newValue)}
                />
              </div>
              
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <Label
                  htmlFor="end-date"
                  className="form-label text-muted mb-0"
                  style={{ fontWeight: "400" }}
                >
                  End Date
                </Label>
                <Flatpickr
                  className="form-control"
                  value={endDate}
                  options={{
                    dateFormat: "Y-m-d",
                    // minDate: moment(startDate).add(1, "day").toDate(),
                    maxDate:  moment.min(
                      moment(startDate).add(30, "days"),
                      moment()
                    ).toDate(),
                    allowInput: true,
                  }}
                  disabled={isLoading}
                  onChange={([newValue]: any) => handleEndDateChange(newValue)}
                />
              </div>
            </div>
            {/* <Datepicker
              handleChangeDate={(val: any) => changeDate(val)}
              disabled={isLoading}
            /> */}
          </div>
          <PriceComparisionChart
            title="Market Average Rate"
            data={data}
            isLoading={isLoading}
          />
          {!isLoading && <TrendTable data={data} />}
        </React.Fragment>
      </CardBody>
    </Card>
  );
};

const TrendTable = ({ data }: { data: any }) => {
  const formatTableData = (data: any) => {
    return data
      .map((item: any) => {
        if (item.data && item.data.length > 0) {
          const formatted: any = { id: item.id };
          item.data.forEach((entry: any) => {
            formatted[entry.x] = entry.y.toString();
          });
          return formatted;
        }
        return null;
      })
      .filter((item: any) => item !== null);
  };

  const tableData = formatTableData(data) || [];

  const frozenColumns = [
    {
      name: <span className="font-weight-bold fs-14">Rate Type</span>,
      selector: (row: any) => row?.id,
    },
  ];

  const scrollableColumns = Object.keys(tableData[0] || {})
    .filter((key) => key !== "id")
    .map((dateKey) => ({
      name: dateKey,
      selector: (row: any) => `${Number(row[dateKey]).toFixed(2)}¢`,
    }));

  if (tableData.length === 0) return null;

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#F2F2F2",
      },
    },
  };
  return (
    <div className="combined-table">
      <div className="fronzen-table">
        <DataTable
          columns={frozenColumns}
          data={tableData}
          paginationPerPage={10}
          customStyles={{
            cells: { style: { backgroundColor: "#fafafa" } },
            ...customStyles,
          }}
          pagination={false}
        />
      </div>
      <div className="scrollable-table">
        <DataTable
          columns={scrollableColumns}
          data={tableData}
          paginationPerPage={10}
          customStyles={customStyles}
          pagination={false}
        />
      </div>
    </div>
  );
};

export default MarketAvgRateUsn;
